import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';

/**
 * Canon Campaign Report — slide-style replica deck. Renders multiple slides
 * (one per Canon sub-campaign theme) on the same page. KPI numbers come
 * from /api/canon-report/summary; insights paragraph is produced on demand
 * via /api/canon-report/insights (OpenAI gpt-4o-mini).
 *
 * Each <SlideReport> has its own independent state:
 *   - selected campaigns, date range, summary fetch, insights text, creative
 *     upload slots, report title.
 * The parent loads the campaign list once and passes it down.
 */

const API_BASE = 'https://api.glowytics.com';

const CANON_RED = '#CC0000';
const CANON_RED_DARK = '#A30000';

function fmtBigNum(n) {
  if (n == null) return '–';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace('.0', '')}M`;
  if (n >= 10_000) return `${Math.round(n / 1000)}K`;
  if (n >= 1_000) return `${(n / 1000).toFixed(1).replace('.0', '')}K`;
  return new Intl.NumberFormat('tr-TR').format(Math.round(n));
}
function fmtMoney(n, ccy) {
  if (n == null) return '–';
  try {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: ccy || 'TRY', maximumFractionDigits: 0 }).format(n);
  } catch {
    return `${Math.round(n).toLocaleString('tr-TR')} ${ccy || ''}`.trim();
  }
}
function fmtPct(n, digits = 2) {
  if (n == null) return '–';
  return `${(n * 100).toFixed(digits)}%`;
}

// Türkçe diakritikleri sadeleştir (İ/ç/ö/ş/ğ eşleştirme sorunlarını çözer)
const norm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

// Bir kampanyayı temasına göre sınıfla. SIRA ÖNEMLİ: çekim atölyesi kampanyaları
// isimlerinde "HayalEt" de geçirdiği için önce çekim grubu kontrol edilir.
function classifyCampaign(name) {
  const n = norm(name);
  // Çekim Atölyesi: SADECE isminde "ÇEKİMİ" geçen kampanyalar.
  if (n.includes('cekimi')) return 'cekim';
  // İFSAK: kalan atölye/etkinlik kampanyaları (dügün, CamKöşk, ifsak, atölye).
  if (n.includes('ifsak') || n.includes('camkosk') || n.includes('dugun') || n.includes('atolye')) return 'ifsak';
  if (n.includes('valuablepaper') || n.includes('degerli') || n.includes('megatank')) return 'degerli';
  if (n.includes('rival')) return 'rival';
  if (n.includes('hayalet')) return 'hayalet';
  return 'diger';
}

// Sayfada gösterilecek slide preset'leri — gösterim sırasıyla.
const SLIDE_PRESETS = [
  { key: 'hayalet', title: 'HAYAL ET KAMPANYASI', placeholder: 'Hayal Et' },
  { key: 'cekim',   title: 'ÇEKİM ATÖLYESİ',      placeholder: 'Çekim Atölyesi' },
  { key: 'ifsak',   title: 'İFSAK',                placeholder: 'İFSAK' },
  { key: 'degerli', title: 'ValuablePaper',         placeholder: 'ValuablePaper' },
  { key: 'rival',   title: 'RAKİP KAMPANYALARI',   placeholder: 'Rakip' },
  { key: 'diger',   title: 'DİĞER KAMPANYALAR',    placeholder: 'Diğer' },
];

// ──────────────────────────────────────────────────────────────────────────
// Page — loads campaigns once, renders multiple slides.
// ──────────────────────────────────────────────────────────────────────────

export default function CanonCampaignReport() {
  const [campaigns, setCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [defaultFrom, setDefaultFrom] = useState('');
  const [defaultTo, setDefaultTo] = useState('');
  const [pageError, setPageError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoadingCampaigns(true);
      try {
        const r = await fetch(`${API_BASE}/api/canon-report/campaigns`, {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });
        const data = await r.json();
        if (!alive) return;
        if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
        setCampaigns(data.campaigns || []);
        const all = data.campaigns || [];
        if (all.length > 0) {
          const minD = all.reduce((m, c) => (c.firstDate && (!m || c.firstDate < m) ? c.firstDate : m), null);
          const maxD = all.reduce((m, c) => (c.lastDate && (!m || c.lastDate > m) ? c.lastDate : m), null);
          if (minD) setDefaultFrom(minD);
          if (maxD) setDefaultTo(maxD);
        }
      } catch (err) {
        setPageError(err.message);
      } finally {
        if (alive) setLoadingCampaigns(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Kampanyaları temalarına göre grupla; her dolu grup bir slide olur (preset sırasıyla).
  const slides = useMemo(() => {
    const byKey = {};
    for (const c of campaigns) {
      const k = classifyCampaign(c.name);
      (byKey[k] = byKey[k] || []).push(c.id);
    }
    return SLIDE_PRESETS
      .map((p) => ({ ...p, ids: byKey[p.key] || [] }))
      .filter((p) => p.ids.length > 0);
  }, [campaigns]);

  return (
    <div style={{ background: '#f3f3f3', minHeight: '100vh', padding: 24 }}>
      {pageError && (
        <div style={{ color: '#900', background: '#fee', padding: 10, borderRadius: 4, marginBottom: 12 }}>
          {pageError}
        </div>
      )}

      {slides.length === 0 && !loadingCampaigns && (
        <div style={{ color: '#888', textAlign: 'center', padding: 40 }}>Kampanya bulunamadı.</div>
      )}
      {slides.map((s, i) => (
        <React.Fragment key={s.key}>
          {i > 0 && <div style={{ height: 32 }} />}
          <SlideReport
            defaultTitle={s.title}
            placeholder={s.placeholder}
            presetIds={s.ids}
            campaigns={campaigns}
            loadingCampaigns={loadingCampaigns}
            defaultFrom={defaultFrom}
            defaultTo={defaultTo}
          />
        </React.Fragment>
      ))}

      <style>{`
        @media print {
          body { background: white; }
          .no-print, [data-controls-bar] { display: none !important; }
          .image-slot-empty { visibility: hidden; border-color: transparent !important; background: transparent !important; }
          @page { size: A4 landscape; margin: 10mm; }
        }
      `}</style>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Single slide — owns its own filters, summary, insights, images.
// ──────────────────────────────────────────────────────────────────────────

function SlideReport({ defaultTitle, placeholder, presetIds, campaigns, loadingCampaigns, defaultFrom, defaultTo }) {
  // Preset slide ise bu grubun kampanyaları başta otomatik seçili gelir; kullanıcı sonradan değiştirebilir.
  const [selectedCampaignIds, setSelectedCampaignIds] = useState(() => presetIds || []);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const [summary, setSummary] = useState(null);
  const [insights, setInsights] = useState('');
  const [reportTitle, setReportTitle] = useState(defaultTitle);

  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [error, setError] = useState('');

  const [creativeImages, setCreativeImages] = useState([null, null]);

  // Pick up page-level defaults when they arrive
  useEffect(() => { if (defaultFrom && !from) setFrom(defaultFrom); }, [defaultFrom, from]);
  useEffect(() => { if (defaultTo && !to) setTo(defaultTo); }, [defaultTo, to]);

  const headers = useCallback(() => ({ 'Content-Type': 'application/json' }), []);

  const loadSummary = useCallback(async () => {
    if (!from || !to) return;
    setLoadingSummary(true); setError('');
    try {
      const qs = new URLSearchParams({ from, to });
      if (selectedCampaignIds.length > 0) qs.set('campaignIds', selectedCampaignIds.join(','));
      const r = await fetch(`${API_BASE}/api/canon-report/summary?${qs.toString()}`, {
        credentials: 'include', headers: headers(),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
      setSummary(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingSummary(false);
    }
  }, [from, to, selectedCampaignIds, headers]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const toggleCampaign = (id) => {
    setSelectedCampaignIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const selectAllCampaigns = () => setSelectedCampaignIds(campaigns.map((c) => c.id));
  const clearCampaigns = () => setSelectedCampaignIds([]);

  const [exporting, setExporting] = useState(false);
  const exportExcel = async () => {
    if (!from || !to || exporting) return;
    setExporting(true); setError('');
    try {
      const qs = new URLSearchParams({ from, to, title: reportTitle });
      if (selectedCampaignIds.length > 0) qs.set('campaignIds', selectedCampaignIds.join(','));
      const r = await fetch(`${API_BASE}/api/canon-report/export?${qs.toString()}`, {
        credentials: 'include',
      });
      if (!r.ok) {
        let msg = `HTTP ${r.status}`;
        try { msg = (await r.json()).error || msg; } catch { /* binary */ }
        throw new Error(msg);
      }
      const blob = await r.blob();
      const cd = r.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="?([^"]+)"?/);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = m ? m[1] : 'canon-report.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setExporting(false);
    }
  };

  const generateInsights = async () => {
    if (!summary) return;
    setLoadingInsights(true); setError('');
    try {
      const r = await fetch(`${API_BASE}/api/canon-report/insights`, {
        method: 'POST', credentials: 'include', headers: headers(),
        body: JSON.stringify({
          kpi: summary.kpi,
          campaign: summary.campaign,
          dateRange: summary.dateRange,
          language: 'tr',
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
      setInsights(data.insights || '');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingInsights(false);
    }
  };

  const k = summary?.kpi || {};
  const tiles = [
    { label: 'PAID\nIMPRESSIONS', value: fmtBigNum(k.paidImpressions) },
    { label: 'PAID REACH', value: fmtBigNum(k.paidReach), hint: 'günlük reach toplamı (yaklaşık)' },
    { label: 'PAID\nENGAGEMENT', value: fmtBigNum(k.paidEngagement) },
    { label: 'ENGAGEMENT\nRATE', value: fmtPct(k.engagementRate) },
    { label: 'BUDGET', value: fmtMoney(k.budget, k.currency), hint: 'planlanan' },
    { label: 'COST', value: fmtMoney(k.cost, k.currency), hint: 'gerçekleşen' },
    { label: 'POST', value: k.postCount != null ? new Intl.NumberFormat('tr-TR').format(k.postCount) : '–' },
  ];

  return (
    <>
      {/* Controls */}
      <div style={controlsBar} data-controls-bar="1">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
          <div>
            <label style={lbl}>Rapor Başlığı</label>
            <input value={reportTitle} onChange={(e) => setReportTitle(e.target.value)} style={{ ...inp, width: 320 }} />
          </div>
          <div style={{ minWidth: 320 }}>
            <label style={lbl}>
              Kampanyalar ({selectedCampaignIds.length === 0 ? 'tümü' : `${selectedCampaignIds.length}/${campaigns.length} seçili`})
              <span style={{ marginLeft: 8 }}>
                <button type="button" onClick={selectAllCampaigns} style={btnTiny}>Tümü</button>
                <button type="button" onClick={clearCampaigns} style={btnTiny}>Temizle</button>
              </span>
            </label>
            <div style={{
              maxHeight: 140, overflowY: 'auto', border: '1px solid #ccc', borderRadius: 4,
              padding: 6, background: 'white', fontSize: 13,
            }}>
              {loadingCampaigns && <div style={{ color: '#888' }}>Yükleniyor…</div>}
              {!loadingCampaigns && campaigns.length === 0 && <div style={{ color: '#888' }}>Kampanya bulunamadı.</div>}
              {campaigns.map((c) => (
                <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={selectedCampaignIds.includes(c.id)}
                    onChange={() => toggleCampaign(c.id)}
                  />
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label style={lbl}>Başlangıç</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={inp} />
          </div>
          <div>
            <label style={lbl}>Bitiş</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={inp} />
          </div>
          <button onClick={loadSummary} disabled={loadingSummary} style={btn}>
            {loadingSummary ? 'Yükleniyor…' : 'Yenile'}
          </button>
          <button onClick={generateInsights} disabled={loadingInsights || !summary} style={{ ...btn, background: CANON_RED }}>
            {loadingInsights ? 'AI üretiliyor…' : 'AI Insights Üret'}
          </button>
          <button onClick={exportExcel} disabled={exporting || !summary} style={{ ...btn, background: '#107C41' }}>
            {exporting ? 'Excel…' : '⬇ Excel'}
          </button>
          <button onClick={() => window.print()} style={btn}>
            PDF / Yazdır
          </button>
        </div>
        {error && (
          <div style={{ marginTop: 10, color: '#900', background: '#fee', padding: 8, borderRadius: 4 }}>{error}</div>
        )}
      </div>

      {/* The "slide" */}
      <div style={slide}>
        <div style={slideHeader}>
          <div>
            <div style={{ fontSize: 32, fontWeight: 800, letterSpacing: 0.5 }}>{reportTitle}</div>
            <div style={{ fontSize: 14, color: '#555', marginTop: 4, letterSpacing: 1 }}>ACTIVITY PERFORMANCE</div>
          </div>
          <div style={{ textAlign: 'right', fontSize: 12, color: '#888' }}>
            {summary?.dateRange ? `${summary.dateRange.from} → ${summary.dateRange.to}` : ''}
            <div>{summary?.campaign?.name || ''}</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 32, marginTop: 24 }}>
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8 }}>
              {tiles.map((t, i) => (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <div style={{
                    background: CANON_RED, color: 'white', width: '100%',
                    aspectRatio: '1 / 1', borderRadius: 6,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 10, textAlign: 'center', whiteSpace: 'pre-line',
                    fontWeight: 700, padding: 4,
                    boxShadow: 'inset 0 0 0 1px ' + CANON_RED_DARK,
                  }}>
                    {t.label}
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#111', marginTop: 4, textAlign: 'center' }}>
                    {t.value}
                  </div>
                  {t.hint && <div style={{ fontSize: 9, color: '#888', textAlign: 'center' }}>{t.hint}</div>}
                </div>
              ))}
            </div>

            <div style={{ marginTop: 28 }}>
              <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 8 }}>Overall Insights</div>
              {insights ? (
                <InsightMarkdown text={insights} />
              ) : (
                <div style={{ color: '#888', fontSize: 13, fontStyle: 'italic' }}>
                  "AI Insights Üret" butonuna basarak GlowyAI'dan kampanya yorumlarını otomatik üretebilirsin.
                  Ürettikten sonra metni direkt slayt üzerinde düzenleyebilirsin.
                </div>
              )}
              {insights && (
                <textarea
                  value={insights}
                  onChange={(e) => setInsights(e.target.value)}
                  rows={Math.min(12, Math.max(4, insights.split('\n').length))}
                  className="no-print"
                  style={{
                    marginTop: 12, width: '100%', fontSize: 12, fontFamily: 'system-ui, sans-serif',
                    border: '1px dashed #ccc', borderRadius: 4, padding: 8, color: '#444',
                  }}
                />
              )}
            </div>
          </div>

          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {creativeImages.map((dataUrl, idx) => (
                <ImageSlot
                  key={idx}
                  value={dataUrl}
                  placeholder={placeholder}
                  onChange={(url) => {
                    setCreativeImages((prev) => prev.map((v, i) => i === idx ? url : v));
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        <div style={slideFooter}>
          <img src="/Canon_logo_vector.png" alt="Canon" style={{ height: 34, width: 'auto', objectFit: 'contain' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ fontSize: 10, color: '#888', textAlign: 'right' }}>
              {new Date().toLocaleDateString('en-US')} · R1 · Confidential
            </div>
            <img src="/Jellybon-digital-agency-istanbul.webp" alt="Jellybon" style={{ height: 30, width: 'auto', objectFit: 'contain' }} />
          </div>
        </div>
      </div>
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────────

function ImageSlot({ value, onChange, placeholder }) {
  const inputRef = useRef(null);
  const dragRef = useRef(null);

  const readFile = (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = (e) => onChange(e.target.result);
    reader.readAsDataURL(file);
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (f) readFile(f);
    e.target.value = '';
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (dragRef.current) dragRef.current.style.borderColor = '#ddd';
    const f = e.dataTransfer.files?.[0];
    if (f) readFile(f);
  };

  if (value) {
    return (
      <div style={{
        position: 'relative', aspectRatio: '9 / 16', borderRadius: 6, overflow: 'hidden',
        background: '#000',
      }}>
        <img src={value} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        <button
          type="button"
          onClick={() => onChange(null)}
          className="no-print"
          style={{
            position: 'absolute', top: 4, right: 4, width: 22, height: 22,
            borderRadius: 11, background: 'rgba(0,0,0,0.6)', color: 'white',
            border: 0, cursor: 'pointer', fontSize: 14, lineHeight: '20px', padding: 0,
          }}
          title="Görseli kaldır"
        >×</button>
      </div>
    );
  }

  return (
    <div
      ref={dragRef}
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); if (dragRef.current) dragRef.current.style.borderColor = '#888'; }}
      onDragLeave={() => { if (dragRef.current) dragRef.current.style.borderColor = '#ddd'; }}
      onDrop={onDrop}
      style={{
        background: '#fafafa', border: '2px dashed #ddd', borderRadius: 6,
        aspectRatio: '9 / 16', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', padding: 12, textAlign: 'center',
        cursor: 'pointer', color: '#888', fontSize: 12,
      }}
      className="image-slot-empty"
    >
      <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: 1, color: '#bbb' }}>{placeholder || 'Görsel'}</div>
      <input ref={inputRef} type="file" accept="image/*" onChange={onPick} style={{ display: 'none' }} />
    </div>
  );
}

function InsightMarkdown({ text }) {
  const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
  return (
    <ul style={{ paddingLeft: 20, margin: 0, lineHeight: 1.6, fontSize: 13, color: '#333' }}>
      {lines.map((l, i) => {
        const cleaned = l.replace(/^[-*]\s*/, '');
        const bold = cleaned.replace(/\*\*(.+?)\*\*/g, '<b>$1</b>');
        return <li key={i} dangerouslySetInnerHTML={{ __html: bold }} />;
      })}
    </ul>
  );
}

const controlsBar = {
  background: 'white', padding: 14, borderRadius: 8, marginBottom: 16,
  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
};

const slide = {
  background: 'white',
  width: '100%', maxWidth: 1100,
  margin: '0 auto',
  padding: 32,
  boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
  borderRadius: 6,
  fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
  color: '#111',
};
const slideHeader = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
  borderBottom: `4px solid ${CANON_RED}`, paddingBottom: 8,
};
const slideFooter = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  marginTop: 28, paddingTop: 14, borderTop: '1px solid #eee',
};

const lbl = { display: 'block', fontSize: 11, color: '#666', marginBottom: 4 };
const inp = { padding: '6px 8px', borderRadius: 4, border: '1px solid #ccc', fontSize: 13, boxSizing: 'border-box' };
const btn = {
  background: '#111', color: 'white', border: 0,
  padding: '10px 14px', borderRadius: 4, cursor: 'pointer', fontSize: 13,
};
const btnTiny = {
  background: 'white', color: '#444', border: '1px solid #ccc',
  padding: '1px 6px', borderRadius: 3, cursor: 'pointer', fontSize: 10, marginLeft: 4,
};
